import { useMemo, useState } from 'react';
import { Card, Col, Row, Form, Nav, Button, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import LoadingContainer from 'components/common/LoadingContainer';
import CompleteAlertModal from 'components/navbar/top/CompleteAlertModal';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useCompleteNotification,
  useReopenNotification
} from 'hooks/useNotifications';
import AlertCard from './AlertCard';
import { TYPE_OPTIONS, TYPE_BADGES } from './alertMeta';

// The bell dropdown shows the latest few; this page shows everything with
// filters and paging, and is where alerts get managed in bulk.
const PAGE_SIZE = 20;
const FULL_LIMIT = 500;

// First, last and the pages around the current one (null = a gap), so the
// pager fits on a phone however many pages there are
const pageWindow = (current, count) => {
  const pages = [];
  for (let p = 1; p <= count; p++) {
    if (p === 1 || p === count || Math.abs(p - current) <= 1) pages.push(p);
    else if (pages[pages.length - 1] !== null) pages.push(null);
  }
  return pages;
};

const AlertsPage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('open'); // 'open' | 'completed'
  const [typeFilter, setTypeFilter] = useState('');
  const [readFilter, setReadFilter] = useState('all'); // all | unread | read
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { notifications: open, isLoading: loadingOpen } = useNotifications(
    FULL_LIMIT,
    false
  );
  const { notifications: completed, isLoading: loadingCompleted } =
    useNotifications(FULL_LIMIT, true);

  const { markRead } = useMarkNotificationRead();
  const { markAllRead } = useMarkAllNotificationsRead();
  const { completeAlert, isCompleting } = useCompleteNotification();
  const { reopenAlert } = useReopenNotification();
  const [alertToComplete, setAlertToComplete] = useState(null);

  const counts = useMemo(() => {
    const unread = open.filter(n => !n.is_read).length;
    return {
      unread,
      read: open.length - unread,
      open: open.length,
      completed: completed.length
    };
  }, [open, completed]);

  // Per-type counts for the open list drive the category chips
  const typeCounts = useMemo(() => {
    const map = {};
    open.forEach(n => {
      map[n.type] = (map[n.type] || 0) + 1;
    });
    return map;
  }, [open]);

  const source = view === 'open' ? open : completed;

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return source.filter(n => {
      if (typeFilter && n.type !== typeFilter) return false;
      if (view === 'open' && readFilter === 'unread' && n.is_read) return false;
      if (view === 'open' && readFilter === 'read' && !n.is_read) return false;
      if (term && !`${n.title} ${n.body}`.toLowerCase().includes(term))
        return false;
      return true;
    });
  }, [source, typeFilter, readFilter, search, view]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const resetPaging = () => setPage(1);

  const handleOpen = notification => {
    if (!notification.is_read) markRead(notification.id);
    if (notification.link_path) navigate(notification.link_path);
  };

  const handleConfirmComplete = notes => {
    completeAlert(
      { notificationId: alertToComplete.id, notes },
      { onSuccess: () => setAlertToComplete(null) }
    );
  };

  if (loadingOpen || loadingCompleted) return <LoadingContainer />;

  return (
    <>
      <Card className="mb-3">
        <Card.Header>
          <Flex
            justifyContent="between"
            alignItems="center"
            className="flex-wrap gap-2"
          >
            <div>
              <h5 className="mb-1">
                <FontAwesomeIcon icon="bell" className="me-2" />
                Alerts
              </h5>
              <div className="fs--1 text-600">
                <span className="fw-semi-bold text-primary">
                  {counts.unread} unread
                </span>
                {' · '}
                {counts.read} read
                {' · '}
                {counts.open} open
                {' · '}
                {counts.completed} completed
              </div>
            </div>
            {view === 'open' && counts.unread > 0 && (
              <Button
                variant="falcon-default"
                size="sm"
                onClick={() => markAllRead()}
              >
                Mark all as read
              </Button>
            )}
          </Flex>
        </Card.Header>
        <Card.Body className="pt-0">
          <Nav variant="tabs" className="mb-3 flex-nowrap">
            <Nav.Item>
              <Nav.Link
                active={view === 'open'}
                onClick={() => {
                  setView('open');
                  resetPaging();
                }}
              >
                Open{' '}
                <SoftBadge pill bg="primary" className="ms-1">
                  {counts.open}
                </SoftBadge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={view === 'completed'}
                onClick={() => {
                  setView('completed');
                  resetPaging();
                }}
              >
                Completed{' '}
                <SoftBadge pill bg="secondary" className="ms-1">
                  {counts.completed}
                </SoftBadge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Row className="g-2 align-items-end">
            <Col xs={12} md={4}>
              <Form.Control
                size="sm"
                placeholder="Search alerts..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  resetPaging();
                }}
              />
            </Col>
            <Col xs={view === 'open' ? 6 : 12} md={4}>
              <Form.Select
                size="sm"
                value={typeFilter}
                onChange={e => {
                  setTypeFilter(e.target.value);
                  resetPaging();
                }}
              >
                <option value="">All categories</option>
                {TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                    {typeCounts[opt.value] ? ` (${typeCounts[opt.value]})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {view === 'open' && (
              <Col xs={6} md={4}>
                <Form.Select
                  size="sm"
                  value={readFilter}
                  onChange={e => {
                    setReadFilter(e.target.value);
                    resetPaging();
                  }}
                >
                  <option value="all">Read and unread</option>
                  <option value="unread">Unread only</option>
                  <option value="read">Read only</option>
                </Form.Select>
              </Col>
            )}
          </Row>

          {/* Category chips: quick way to shrink the list to one kind of alert */}
          {view === 'open' && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              {Object.entries(typeCounts).map(([type, count]) => (
                <SoftBadge
                  key={type}
                  pill
                  bg={TYPE_BADGES[type]?.bg || 'secondary'}
                  className={`cursor-pointer ${
                    typeFilter === type ? 'border border-dark' : ''
                  }`}
                  role="button"
                  onClick={() => {
                    setTypeFilter(typeFilter === type ? '' : type);
                    resetPaging();
                  }}
                >
                  {TYPE_BADGES[type]?.label || type} {count}
                </SoftBadge>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {pageItems.length === 0 ? (
            <div className="text-center py-5 text-muted">
              {view === 'open' ? 'No open alerts' : 'No completed alerts'}
              {(typeFilter || search || readFilter !== 'all') &&
                ' match these filters'}
            </div>
          ) : (
            pageItems.map(n => (
              <AlertCard
                key={n.id}
                notification={n}
                completed={view === 'completed'}
                onOpen={handleOpen}
                onComplete={setAlertToComplete}
                onReopen={a => reopenAlert({ notificationId: a.id })}
                className={
                  n.is_read || view === 'completed' ? '' : 'bg-soft-primary'
                }
              />
            ))
          )}
        </Card.Body>
        {pageCount > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fs--1 text-600">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, filtered.length)} of{' '}
              {filtered.length}
            </span>
            <Pagination size="sm" className="mb-0">
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              />
              {pageWindow(currentPage, pageCount).map((p, i) =>
                p === null ? (
                  <Pagination.Ellipsis key={`gap-${i}`} disabled />
                ) : (
                  <Pagination.Item
                    key={p}
                    active={p === currentPage}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Pagination.Item>
                )
              )}
              <Pagination.Next
                disabled={currentPage === pageCount}
                onClick={() => setPage(currentPage + 1)}
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      <CompleteAlertModal
        show={!!alertToComplete}
        alert={alertToComplete}
        onHide={() => setAlertToComplete(null)}
        onConfirm={handleConfirmComplete}
        isCompleting={isCompleting}
      />
    </>
  );
};

export default AlertsPage;
